import {
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
} from "@chakra-ui/react";
import { RefObject, useEffect, useRef } from "react";
import { useEnsureFocus } from "../../../../hooks/useEnsureFocus";
import { useScrollbarWidth } from "../../../../utils/scrollbarWidth";
import { ErrorBoundary } from "../../../errors/ErrorBoundary";
import { PopoverSearchBar } from "../../../fields/SearchField";
import { SearchResponse } from "../../api/globalSearchApi";
import { SearchResults } from "./SearchResults";
import { LargeSearchTrigger, SmallSearchTrigger } from "./SearchTrigger";
import { useIsSidebarOpen } from "../../../../hooks/useIsSidebarOpen";
import { useTopBarHeight } from "../../../../hooks/userTopBarHeight";

interface SmallSearchPopoverProps {
  data?: SearchResponse;
  handleSearch: (term: string) => void;
  isFetching?: boolean;
  popoverRef: RefObject<HTMLElement>;
  searchTerm: string;
}

export function SmallSearchPopover({
  data,
  handleSearch,
  isFetching,
  popoverRef,
  searchTerm,
}: SmallSearchPopoverProps) {
  const topBarHeight = useTopBarHeight();
  const scrollbarWidth = useScrollbarWidth();
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const ensureFocus = useEnsureFocus();
  const { setIsSidebarOpen } = useIsSidebarOpen();

  // When the popover appears, the search box should be focused.
  // However, the input's container is still animating and hidden,
  // so it can't be focused immediately. This code intercepts the
  // focus call and ensures the input gains focus once it is visible.
  const focusHandler = useRef({
    focus: () => {
      if (!inputRef.current) {
        return;
      }
      ensureFocus(inputRef.current);
    },
  });

  return (
    <Popover
      gutter={1}
      variant="navbar"
      closeOnBlur={false}
      initialFocusRef={focusHandler}
    >
      {({ onClose, isOpen }) => (
        <ClickOutsideHandler
          popoverRef={popoverRef}
          triggerRef={triggerRef}
          isOpen={isOpen}
          onClose={onClose}
        >
          <PopoverTrigger>
            <SmallSearchTrigger
              ref={triggerRef}
              onClick={() => setIsSidebarOpen(false)}
            />
          </PopoverTrigger>
          <PopoverContent
            className="navbar-popover"
            rootProps={{
              zIndex: 2,
            }}
            variants={{
              enter: { opacity: 1, top: -1, right: 0 },
              exit: { opacity: 0, top: -50, right: 0 },
            }}
            // Left 0 is needed when right is 0 and we should stretch to fullscreen
            left={0}
            w={{ base: "100vw", lg: "100%" }}
            maxHeight={`calc(100vh - ${topBarHeight.get()}px)`}
            mr={scrollbarWidth + 1 + "px"}
            p={2}
            borderRight="1px solid transparent.white.10"
            background="transparent.black.90"
            borderTopRadius={0}
            borderRadius="0 0 24px 24px"
            border="1px solid"
            borderBottom="none"
            borderColor="transparent.white.10"
            backdropFilter="blur(18px)"
            ref={popoverRef}
            style={{ overscrollBehavior: "contain" }}
            overflow="hidden"
          >
            <PopoverSearchBar
              isOpen={true}
              inputRef={inputRef}
              handleSearch={handleSearch}
            />
            <PopoverBody
              p={0}
              maxHeight={`calc(100vh - ${topBarHeight.get()}px - 80px)`}
              overflowY="auto"
              overscrollBehavior="contain"
              onTouchStart={(e) => {
                e.nativeEvent.stopImmediatePropagation();
                inputRef.current?.blur();
              }}
            >
              {isFetching ? (
                <Flex justifyContent="center" alignItems="center">
                  <Spinner color="blaze.blaze" size="md" />
                </Flex>
              ) : data ? (
                <ErrorBoundary>
                  <SearchResults
                    results={data}
                    searchTerm={searchTerm}
                    onNavigate={onClose}
                  />
                </ErrorBoundary>
              ) : null}
            </PopoverBody>
          </PopoverContent>
        </ClickOutsideHandler>
      )}
    </Popover>
  );
}

function ClickOutsideHandler({
  popoverRef,
  triggerRef,
  isOpen,
  onClose,
  children,
}: {
  popoverRef: RefObject<HTMLElement>;
  triggerRef: RefObject<HTMLElement>;
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      const isInsidePopover = popoverRef.current?.contains(target);
      const isInsideTrigger = triggerRef.current?.contains(target);

      if (!isInsidePopover && !isInsideTrigger) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen, onClose, popoverRef, triggerRef]);

  return <>{children}</>;
}

interface LargeSearchPopoverProps {
  data?: SearchResponse;
  handleSearch: (term: string) => void;
  isOpen?: boolean;
  isFetching?: boolean;
  onClose?: () => void;
  popoverRef: React.RefObject<HTMLElement>;
  searchTerm: string;
}
export function LargeSearchPopover({
  data,
  handleSearch,
  isOpen = false,
  isFetching = false,
  onClose,
  popoverRef,
  searchTerm = "",
}: LargeSearchPopoverProps) {
  return (
    <Popover
      gutter={1}
      variant="searchResults"
      autoFocus={false}
      isOpen={isOpen}
      placement="bottom-start"
      matchWidth={true}
    >
      <PopoverTrigger>
        <LargeSearchTrigger handleSearch={handleSearch} resultsOpen={isOpen} />
      </PopoverTrigger>
      <PopoverContent
        className="navbar-popover"
        rootProps={{
          zIndex: 2,
        }}
        style={{
          transition: "none",
          overscrollBehavior: "contain",
        }}
        variants={{
          enter: {
            opacity: 1,
            maxHeight: 400,
            top: -5,
          },
          exit: {
            opacity: 0,
            maxHeight: 0,
          },
        }}
        // Match width to search trigger field
        w="100%"
        // Emulating padding since we're unable to style overflow container
        //borderRight="16px solid"
        ref={popoverRef}
      >
        <PopoverBody className="styled-scrollbar-hover">
          {isFetching ? (
            <Flex justifyContent="center" alignItems="center">
              <Spinner color="blaze.blaze" size="md" />
            </Flex>
          ) : data ? (
            <ErrorBoundary>
              <SearchResults results={data} searchTerm={searchTerm} onNavigate={onClose} />
            </ErrorBoundary>
          ) : null}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
